import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Tooltip from '@mui/material/Tooltip';

interface LogEntry {
  id: number;
  userId: number;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

interface Props {
  module: string;
  entityId?: number;
  users?: any[];
  /** Map of system field name → human-readable label */
  fieldLabels?: Record<string, string>;
  maxHeight?: number | string;
}

const ACTION_COLOR: Record<string, string> = {
  create: '#43c98a',
  update: '#4a90e2',
  delete: '#f76f8e',
};

const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const color = ACTION_COLOR[action] ?? 'var(--text-color-calendar)';
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
      color,
      background: color + '22',
      borderRadius: 4,
      padding: '2px 6px',
    }}>
      {action}
    </span>
  );
};

function isIsoDate(val: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T/.test(val) || /^\d{4}-\d{2}-\d{2}$/.test(val);
}

function fmtDate(val: string): string {
  try {
    return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return val; }
}

const ActivityLogPanel: React.FC<Props> = ({ module, entityId, users = [], fieldLabels = {}, maxHeight = 400 }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const getUserName = useCallback((idStr: string | null): string | null => {
    if (!idStr) return null;
    const id = Number(idStr);
    if (isNaN(id)) return null;
    const u = users.find((x: any) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : null;
  }, [users]);

  const resolveValue = useCallback((field: string | null, val: string | null): string => {
    if (val === null || val === undefined) return '—';
    if (!field) return val;

    // Fields that store user IDs
    if (/UserId$/i.test(field)) {
      const name = getUserName(val);
      return name ?? val;
    }

    // Boolean-like values
    if (val === 'true') return 'Yes';
    if (val === 'false') return 'No';

    // ISO dates
    if (isIsoDate(val)) return fmtDate(val);

    return val;
  }, [getUserName]);

  const resolveField = (field: string | null): string => {
    if (!field) return '—';
    if (fieldLabels[field]) return fieldLabels[field];
    // Fallback: convert camelCase/snake_case to words
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^\w/, c => c.toUpperCase());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { module, limit: 300 };
      if (entityId) params.entityId = entityId;
      const { data } = await axios.get('activity-log', { params });
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [module, entityId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-l2)', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>
        {t('Loading')}...
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div style={{ padding: 'var(--space-l2)', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>
        {t('Empty')}
      </div>
    );
  }

  const getUserParts = (id: number): { first: string; last: string } => {
    const u = users.find((x: any) => x.id === id);
    if (u) return { first: u.first_name ?? '', last: u.last_name ?? '' };
    return { first: `#${id}`, last: '' };
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-color)',
    background: 'var(--background-color)',
    borderBottom: 'var(--border-input)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };

  const cellClip: React.CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };

  // 2-line clamp: wraps up to 2 lines then shows ellipsis
  const cell2: React.CSSProperties = {
    overflow: 'hidden',
    display: '-webkit-box' as any,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
    wordBreak: 'break-word',
  };

  return (
    <div style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight, fontSize: 13 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '13%' }} />{/* Date */}
          <col style={{ width: '13%' }} />{/* User */}
          <col style={{ width: '11%' }} />{/* Action */}
          <col style={{ width: '20%' }} />{/* Field */}
          <col style={{ width: '21%' }} />{/* Old */}
          <col style={{ width: '22%' }} />{/* New */}
        </colgroup>
        <thead>
          <tr>
            {['Date', 'User', 'Action', 'Field', 'Old', 'New'].map(h => (
              <th key={h} style={thStyle}>{t(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => {
            const p = getUserParts(log.userId);
            const fieldLabel = resolveField(log.field);
            const oldVal = resolveValue(log.field, log.oldValue);
            const newVal = resolveValue(log.field, log.newValue);
            const fullDate = new Date(log.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            const fullTime = new Date(log.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return (
              <tr key={log.id} style={{ borderBottom: '1px solid var(--border-input-two)' }}>
                <td style={{ padding: '5px 10px', color: 'var(--text-color-calendar)', lineHeight: 1.4 }}>
                  <div style={cellClip}>{fullDate}</div>
                  <div style={{ ...cellClip, fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{fullTime}</div>
                </td>
                <td style={{ padding: '5px 10px', color: 'var(--text-color)', lineHeight: 1.4 }}>
                  <div style={cellClip}>{p.first}</div>
                  {p.last && <div style={cellClip}>{p.last}</div>}
                </td>
                <td style={{ padding: '5px 10px' }}>
                  <ActionBadge action={log.action} />
                </td>
                <td style={{ padding: '5px 10px', color: 'var(--text-color-calendar)' }}>
                  <Tooltip title={fieldLabel} placement="top" arrow disableInteractive enterDelay={300}>
                    <span style={{ display: 'block', ...cell2 }}>{fieldLabel}</span>
                  </Tooltip>
                </td>
                <td style={{ padding: '5px 10px', color: 'var(--text-color-calendar-week)' }}>
                  {oldVal === '—' ? '—' : (
                    <Tooltip title={oldVal} placement="top" arrow disableInteractive enterDelay={300}>
                      <span style={{ display: 'block', ...cell2 }}>{oldVal}</span>
                    </Tooltip>
                  )}
                </td>
                <td style={{ padding: '5px 10px', color: 'var(--text-color)', fontWeight: 600 }}>
                  {newVal === '—' ? '—' : (
                    <Tooltip title={newVal} placement="top" arrow disableInteractive enterDelay={300}>
                      <span style={{ display: 'block', ...cell2 }}>{newVal}</span>
                    </Tooltip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ActivityLogPanel;
